import type { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { useSortable } from '@dnd-kit/sortable';
import {
  faAt,
  faFileLines,
  faFilePdf,
  faLink,
  faPhone,
} from '@fortawesome/pro-light-svg-icons';
import { faSquareRss } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IHorizontalCardProps } from '~/components/molecules/HorizontalCard';
import type { IApiGetCategoryListItemResponseBody } from '~/lib/api/modules/Categories';
import { HorizontalCard } from '~/components/molecules/HorizontalCard';
import { css } from '~/helpers/css';
import { getActionTypeFromTarget } from '~/helpers/getActionTypeFromTarget';
import { ITimeUnit } from '~/helpers/types';
import { useDeleteCategoryMutation } from '~/lib/api/modules/Categories/hooks/useDeleteCategoryMutation';
import { CategoryListItemMenu } from './CategoryListItemMenu';

export type ICategoryListItemProps = IHorizontalCardProps & {
  category: IApiGetCategoryListItemResponseBody;
  onDeleteStart?: () => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: (error: unknown) => void;
};

const styles = stylex.create({
  host: {
    position: 'relative',
  },
  titleContainer: {
    marginLeft: 12,
    marginRight: 'calc(40px + 6px + 6px)',
  },
  actions: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 40,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginRight: 6,
  },
});

const getIconFromActionType = (
  actionType: string,
): IconDefinition | undefined => {
  switch (actionType) {
    case 'url':
      return faLink;
    case 'phoneNumber':
      return faPhone;
    case 'emailAddress':
      return faAt;
    case 'article':
      return faFileLines;
    case 'pdf':
      return faFilePdf;
  }

  return undefined;
};

export const CategoryListItem: React.FC<ICategoryListItemProps> = (props) => {
  const {
    sx,
    category,
    onDeleteStart,
    onDeleteSuccess,
    onDeleteError,
    ...other
  } = props;
  const { t } = useTranslation();

  const deleteCategoryMutation = useDeleteCategoryMutation();
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: category._id });

  const handleDelete = (): Promise<unknown> | undefined => {
    // eslint-disable-next-line no-alert
    const doDeletion = confirm(
      t(
        'CategoryListItem.do-you-confirm-the-deletion-of-category-category-name',
        { name: category.name },
      ),
    );

    if (doDeletion) {
      // Delay the deletion to allow the user to see the processing state of his
      // action.
      if (onDeleteStart) {
        setTimeout(() => onDeleteStart(), 600 * ITimeUnit.MILLISECOND);
      }

      return deleteCategoryMutation
        .mutateAsync({
          appId: category.appId,
          categoryId: category._id,
        })
        .then(onDeleteSuccess)
        .catch(onDeleteError);
    }
  };

  const sortableStyle = {
    transform: css.transform.toString(transform),
    transition,
    zIndex: isDragging ? 99 : undefined,
  };

  const isLink = !!category.action;
  const processing = deleteCategoryMutation.isLoading;
  const actionType = category.action
    ? getActionTypeFromTarget(category.action)
    : undefined;
  const actionIcon = actionType ? getIconFromActionType(actionType) : undefined;

  return (
    <div
      {...stylex.props([styles.host, sx])}
      ref={setNodeRef}
      style={sortableStyle}
      {...listeners}
    >
      <HorizontalCard
        image={category.picture?.[0]?.mediumUrl}
        color={category.color}
        icon={actionIcon ? <FontAwesomeIcon icon={actionIcon} /> : undefined}
        href={
          isLink
            ? `/apps/${category.appId}/links/${category._id}/update`
            : `/apps/${category.appId}/categories/${category._id}`
        }
        title={
          <div {...stylex.props(styles.titleContainer)}>
            {category.rssFeedUrl && (
              <>
                <FontAwesomeIcon icon={faSquareRss} />{' '}
              </>
            )}
            {category.name}
          </div>
        }
        dragged={isDragging}
        draft={category.hidden}
        {...other}
      />
      <div {...stylex.props(styles.actions)}>
        <CategoryListItemMenu
          appId={category.appId}
          categoryId={category._id}
          isLink={isLink}
          processing={processing}
          handleDelete={handleDelete}
        />
      </div>
    </div>
  );
};
